import _extends from '@babel/runtime/helpers/extends';
import React__default, { useMemo, useCallback } from 'react';
import PT from 'prop-types';
import { S as StyledTree, a as StyledTreeList, N as NodeWrapper, R as RenderNodeWrapper } from './Tree.styles-1282e99a.js';
import { A as ArrowButton, N as NoArrowSpacer } from './ArrowButton-02d51580.js';
import { D as DepthSpacer } from './DepthSpacer-ffdea263.js';
import { u as useStateEphemeral } from '../LocalModules/storm-ui-utils/dist/esm/useStateEphemeral-58aaa77c-6d3e5093.js';
import { a as TaktIdProvider } from '../TaktIdContext/TaktIdContext-f853b17a.js';
import { c as createStormTaktId } from '../TaktIdContext/TaktIdContext.utils-39f30e88.js';
import { T as Text } from '../Text/Text-f48a0c0d.js';

const renderTreeNode = item => {
  if (item.renderLabel) return item.renderLabel(item.label);
  return /*#__PURE__*/React__default.createElement(Text, null, item.label);
};
const shouldArrowShow = (item, displayed) => {
  let areChildrenDisplayed = false;
  if (item.children && item.children.length > 0) {
    areChildrenDisplayed = true;
    if (displayed) {
      areChildrenDisplayed = false;
      for (let i = 0; i < item.children.length; i++) {
        if (displayed[item.children[i].id] !== undefined) {
          areChildrenDisplayed = true;
          break;
        }
      }
    }
  }
  return areChildrenDisplayed;
};
const getDisplayedChildrenNodes = (currentDisplayed = {}, node, displayAll) => {
  const childrenToDisplay = [];
  if (node.children && node.children.length) {
    const hasVisible = node.children.some(child => (currentDisplayed === null || currentDisplayed === void 0 ? void 0 : currentDisplayed[child.id]) === true);
    node.children.forEach(child => {
      if (displayAll || hasVisible) {
        childrenToDisplay.push(child);
      }
    });
  }
  return childrenToDisplay;
};
const isLastNode = (index, isRoot, lastTree, dataLength, displayedChildren) => {
  if (!displayedChildren && index === dataLength - 1 && (isRoot || lastTree)) {
    return true;
  }
  return false;
};
function Tree(props) {
  const {
    taktId,
    taktValue
  } = props;
  const {
    data,
    role = 'tree',
    displayed,
    renderNode,
    onDisplayChange,
    arrowLabel,
    depth = 0,
    maxDepth = Infinity,
    isLastTree,
    displayAll,
    ...rest
  } = props;
  // gives display control to the local Tree component if the `displayed` prop is used
  const [displayState, setDisplayState] = useStateEphemeral({}, {
    externalState: displayed,
    externalDispatch: onDisplayChange,
    useExternalState: !!displayed
  });
  // the display state must be combined with the root nodes of the tree if no external display state is used
  const displayedNodes = useMemo(() => displayed !== null && displayed !== void 0 ? displayed : data.reduce((acc, topNode) => ({
    ...acc,
    [topNode.id]: true
  }), displayState), [data, displayState, displayed]);
  const isRoot = depth === 0;
  const handleArrowClick = useCallback(item => {
    const displayedChildrenNodes = {};
    if (item.children) {
      for (let i = 0; i < ((_item$children = item.children) === null || _item$children === void 0 ? void 0 : _item$children.length); i++) {
        var _item$children;
        const hasVisible = item.children.some(node => (displayedNodes === null || displayedNodes === void 0 ? void 0 : displayedNodes[node.id]) === true);
        // if displayed is controlled externally
        if (displayed) {
          if (displayed[item.children[i].id] !== undefined) {
            displayedChildrenNodes[item.children[i].id] = !hasVisible;
          }
        } else {
          displayedChildrenNodes[item.children[i].id] = !hasVisible;
        }
      }
    }
    setDisplayState({
      ...displayedNodes,
      ...displayedChildrenNodes
    });
  }, [displayed, displayedNodes, setDisplayState]);
  const showArrows = data.map(item => shouldArrowShow(item, displayed) && depth < maxDepth && !displayAll);
  const hasChildWithArrowButton = showArrows.some(Boolean);
  return /*#__PURE__*/React__default.createElement(TaktIdProvider, {
    taktId: isRoot ? taktId : undefined,
    taktValue: isRoot ? taktValue : undefined,
    fallbackId: isRoot ? createStormTaktId('tree') : undefined
  }, /*#__PURE__*/React__default.createElement(StyledTree, _extends({
    role: role
  }, rest), data.map((item, index) => {
    const displayedChildren = getDisplayedChildrenNodes(displayedNodes, item, displayAll);
    const lastNode = isLastNode(index, role === 'tree', isLastTree !== null && isLastTree !== void 0 ? isLastTree : false, data.length, displayedChildren.length);
    return displayedNodes && displayedNodes[item.id] && /*#__PURE__*/React__default.createElement(StyledTreeList, {
      "data-test-id": role === 'tree' ? `option-root-${item.label}` : undefined,
      role: "treeitem",
      key: item.id,
      $root: role === 'tree',
      $displayed: displayedChildren.length > 0
    }, /*#__PURE__*/React__default.createElement(NodeWrapper, {
      $lastNode: lastNode,
      "data-test-id": `option-wrapper-${item.label}`
    }, /*#__PURE__*/React__default.createElement(DepthSpacer, {
      depth: depth,
      displayAll: displayAll
    }), showArrows[index] ? /*#__PURE__*/React__default.createElement(ArrowButton, {
      item: item,
      opened: displayedChildren.length > 0,
      onArrowClick: handleArrowClick,
      ariaLabel: typeof arrowLabel === 'string' ? arrowLabel : arrowLabel(item.label),
      taktId: taktId
    }) : /*#__PURE__*/React__default.createElement(NoArrowSpacer, {
      displayAll: displayAll,
      hasSiblingWithArrowButton: hasChildWithArrowButton,
      isTreeRole: role === 'tree'
    }), /*#__PURE__*/React__default.createElement(RenderNodeWrapper, null, renderNode ? renderNode(item) : renderTreeNode(item))), item.children && displayedChildren.length > 0 && /*#__PURE__*/React__default.createElement(Tree, _extends({}, rest, {
      data: displayedChildren,
      role: "group",
      displayed: displayed,
      renderNode: renderNode,
      onDisplayChange: onDisplayChange,
      arrowLabel: arrowLabel,
      depth: depth + 1,
      maxDepth: maxDepth,
      isLastTree: index === data.length - 1,
      displayAll: displayAll
    })));
  })));
}
Tree.propTypes = {
  data: PT.arrayOf(PT.shape({
    id: PT.oneOfType([PT.string, PT.number]).isRequired,
    label: PT.string.isRequired
  })).isRequired,
  role: PT.oneOf(['tree', 'group']),
  displayed: PT.objectOf(PT.any),
  // eslint-disable-line react/forbid-prop-types
  renderNode: PT.func,
  onDisplayChange: PT.func,
  arrowLabel: PT.oneOfType([PT.string, PT.func]).isRequired,
  depth: PT.number
};
Tree.defaultProps = {
  role: 'tree',
  displayed: undefined,
  renderNode: undefined,
  onDisplayChange: undefined,
  depth: 0,
  maxDepth: Infinity,
  isLastTree: undefined,
  displayAll: false
};

export { Tree as T };
//# sourceMappingURL=Tree-bf57cf58.js.map
